import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import Loading from 'components/blocks/Loading';
import reduce from 'lodash/reduce';
import { Modal, Input, TextArea } from 'components/ui';
import { MIN_INPUT_LENGTH } from 'constants/common';

class EditProjectModal extends Component {
  state = {
    name: '',
    description: '',
    errors: {},
  };

  componentDidMount() {
    this.getStateFromProps();
  }

  componentDidUpdate(prevProps) {
    this.comparePrevProjectPropsToCurrentProps(prevProps);
    this.resetFormWhenAddingProject(prevProps);
  }

  clearError = key => ({ ...this.state.errors, [key]: '' });

  comparePrevProjectPropsToCurrentProps = prevProps => {
    const { project: prevProject } = prevProps;
    const { project } = this.props;
    const hasId = project.id || project.id === 0;
    if (hasId && !isEqual(prevProject, project)) {
      this.getStateFromProps();
    }
  };

  resetFormWhenAddingProject = prevProps => {
    const { isVisible, project } = this.props;
    const isOpening = !prevProps.isVisible && isVisible;
    const hasId = project.id || project.id === 0;
    if (isOpening && !hasId) this.resetForm();
  };

  getStateFromProps = () => {
    const { project } = this.props;
    const { name = '', description = '' } = project;
    this.setState({ name, description });
  };

  validateProject = () => {
    const { projects } = this.props;
    return !!projects.length;
  };

  handleSaveProject = () => {
    const { id } = this.props.project;
    const { name, description } = this.state;
    const values = { name, description };
    if (this.validateValues(values)) {
      this.props.onSaveProject({ ...values, id });
    }
  };

  validateValues = values => {
    const emptyValues = reduce(
      values,
      (acc, value, key) => {
        if (!value || value.length < MIN_INPUT_LENGTH) acc[key] = value;
        return acc;
      },
      {},
    );
    if (Object.keys(emptyValues).length) {
      this.displayErrors(emptyValues);
      return false;
    }
    return true;
  };

  displayErrors = emptyValues => {
    const errors = reduce(
      emptyValues,
      (acc, value, key) => {
        acc[key] =
          value && value.trim()
            ? `Should have at least ${MIN_INPUT_LENGTH} characters`
            : 'Required';
        return acc;
      },
      {},
    );
    this.setState({ errors });
  };

  handleInputChange = event => {
    const { value, name } = event.target;
    const errors = this.clearError(name);
    this.setState({ [name]: value, errors });
  };

  handleClose = () => {
    this.props.onHide();
  };

  resetForm = () => {
    this.setState({
      name: '',
      description: '',
    });
  };

  render() {
    const { isVisible, isLoading } = this.props;
    const { name, description, errors } = this.state;

    return (
      <Modal
        visible={isVisible}
        onCancel={this.handleClose}
        onOk={this.handleSaveProject}
      >
        {isLoading && <Loading />}
        <div className="project-editor">
          <Input
            error={errors.name}
            helperTxt={errors.name}
            id="project-name-input"
            label="Project Name"
            name="name"
            placeholder="Project name"
            value={name}
            onChange={this.handleInputChange}
          />
        </div>
        <div className="project-editor__textarea">
          <TextArea
            error={errors.description}
            helperTxt={errors.description}
            id="project-description-input"
            label="Description"
            name="description"
            placeholder="Description"
            rows={5}
            value={description}
            onChange={this.handleInputChange}
          />
        </div>
      </Modal>
    );
  }
}

const projectShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  description: PropTypes.string,
});

EditProjectModal.defaultProps = {
  isLoading: false,
  project: {},
  projects: [],
};

EditProjectModal.propTypes = {
  isLoading: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
  project: projectShape,
  projects: PropTypes.arrayOf(projectShape),
  onHide: PropTypes.func.isRequired,
  onSaveProject: PropTypes.func.isRequired,
};

export default EditProjectModal;
